import { ReactNode } from 'react';

import { Grid, Button } from '@simplywallst/ui-core';

import SidebarOutline from '@simplywallst/ui-core/icons/SidebarOutline';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';

import { HoldingsSortingSelect } from './HoldingsSortingSelect';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import { useIsMobileScreen } from '@/hooks/useMedia';

import { TableTitle } from './TableTitle';

import { TableHeader } from './styled';

type Props = {
  portfolio: Portfolio;
  panelHoldingId: Holding['id'] | null;
  main: ReactNode;
  aside: ReactNode;
  onToggleTableExpansion: () => void;
  onUpdatePortfolio?: () => void;
};

export const HoldingsOverviewLayout = ({
  portfolio,
  panelHoldingId,
  main,
  aside,
  onUpdatePortfolio,
  onToggleTableExpansion,
}: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  // NOTE: This is a temporary solution to handle the different types of portfolios
  // until we have a more robust solution for handling different portfolio types
  const isManualHoldingsPortfolio =
    portfolio.type === 'manual' &&
    portfolio.manualInputOption !== 'transactions';
  const canEdit =
    typeof onUpdatePortfolio !== 'undefined' && isManualHoldingsPortfolio;

  return (
    <>
      {isMobileScreen ? (
        <>
          <section data-cy-id="holdings-table">
            <TableHeader
              as="header"
              templateColumns="auto auto"
              justifyContent="space-between"
              gap="8px"
              alignItems="center"
              data-cy-id="holdings-header"
            >
              <Grid templateColumns="auto auto" gap="16px">
                <TableTitle holdingsCount={portfolio.holdings.length} />
                {canEdit && (
                  <Button
                    styleType="passive"
                    alignIcon="left"
                    onClick={onUpdatePortfolio}
                  >
                    <EditSolid /> Edit
                  </Button>
                )}
              </Grid>
              <HoldingsSortingSelect />
            </TableHeader>
            <div className="grid gap-x1_5 pb-x3">{main}</div>
          </section>
          {aside}
        </>
      ) : (
        <Grid
          templateColumnsLg={panelHoldingId ? 'auto 32%' : 'auto'}
          gap="16px"
        >
          <div>
            <section data-cy-id="holdings-table">
              <TableHeader
                as="header"
                templateColumns="auto auto"
                justifyContent="space-between"
                gap="8px"
                alignItems="center"
                data-cy-id="holdings-header"
              >
                <Grid templateColumns="auto auto" gap="16px">
                  <TableTitle holdingsCount={portfolio.holdings.length} />
                  {canEdit && (
                    <Button
                      styleType="passive"
                      alignIcon="left"
                      onClick={onUpdatePortfolio}
                    >
                      <EditSolid /> Edit
                    </Button>
                  )}
                </Grid>
                <Grid templateColumns="max-content max-content" gap="4px">
                  <HoldingsSortingSelect />
                  <Button styleType="passive" onClick={onToggleTableExpansion}>
                    <SidebarOutline aria-label="Expand Holding Table" />
                  </Button>
                </Grid>
              </TableHeader>
              <div className="grid gap-x2 pb-x1">{main}</div>
            </section>
          </div>
          {aside}
        </Grid>
      )}
    </>
  );
};
